import "../../css/Components/Pages/SignIn/SignIn.css";
import Logo from "../../assets/icons/logo-with-title.svg";
import { useNavigate } from "react-router-dom";
import {
  auth,
  provider as googleProvider,
  signInWithPopup,
} from "../../backend/firebaseconfig";
import { OAuthProvider } from "firebase/auth";
import { collection, onSnapshot } from "firebase/firestore";
import { firestore } from "../../backend/firebaseconfig";
import SignInButton from "../DesignSystem/Buttons/SignInButtons";
import GoogleIcon from "../../assets/images/google.png";
import MicrosoftIcon from "../../assets/images/microsoft.png";
import { useUserRole } from "../../context/UserRoleContext";
import { useUserEmail } from "../../context/UserEmailContext";
import { useAdminAdviserMails } from "../../context/AdminAdviserEmailsContext";
import { Helmet } from "react-helmet-async";
import { useEffect, useRef, useState } from "react";
import { canAccessAML } from "../Tools/AML/Utils/permissions";
import { getPortalSectionConfig } from "../PortalSections/utils/portalSectionConfig";

const normaliseEmail = (value = "") => String(value).trim().toLowerCase();

const normalisePermission = (value = "") => String(value).trim().toLowerCase();

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  return [value];
};

const safeParseArray = (value) => {
  try {
    const parsed = JSON.parse(value || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    // console.warn("[SignIn] Failed to parse array from localStorage", {
    //   value,
    //   error,
    // });
    return [];
  }
};

const hasPermission = (permissions = [], permission) => {
  const result = permissions.some(
    (item) => normalisePermission(item) === normalisePermission(permission),
  );

  // console.log("[SignIn] Permission check", {
  //   permission,
  //   permissions,
  //   result,
  // });

  return result;
};

const SignIn = () => {
  const navigate = useNavigate();

  const { setUserRole } = useUserRole();
  const { setUserEmail } = useUserEmail("userEmail");
  const { setAdminAdviserMails } = useAdminAdviserMails();

  const authIndexRef = useRef({
    advisers: new Map(),
    staff: new Set(),
    external: new Map(),
    administrators: new Map(),
    readyRoles: new Set(),
  });

  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    // console.group("[SignIn] Mounting auth index listeners");

    const unsubs = [];

    const watch = (roleKey, colPathParts) => {
      //  console.log("[SignIn] Watching role collection", {
      //   roleKey,
      //   path: colPathParts.join("/"),
      // });

      const colRef = collection(firestore, ...colPathParts);

      const unsub = onSnapshot(
        colRef,
        (snap) => {
          // console.group(`[SignIn] Snapshot received: ${roleKey}`);

          // console.log("[SignIn] Snapshot size", {
          //   roleKey,
          //   size: snap.size,
          // });

          if (roleKey === "advisers") {
            authIndexRef.current.advisers = new Map();
          }

          if (roleKey === "staff") {
            authIndexRef.current.staff = new Set();
          }

          if (roleKey === "external") {
            authIndexRef.current.external = new Map();
          }

          if (roleKey === "administrators") {
            authIndexRef.current.administrators = new Map();
          }

          snap.forEach((d) => {
            const data = d.data() || {};
            const email = normaliseEmail(data.email || d.id);

            if (!email) {
              // console.warn("[SignIn] Skipping document with no email", {
              //   roleKey,
              //   docId: d.id,
              //   data,
              // });
              return;
            }

            if (roleKey === "advisers") {
              authIndexRef.current.advisers.set(email, {
                isAppointed: data.isAppointed,
                isAccessAllowed: data.isAccessAllowed,
              });
            }

            if (roleKey === "staff") {
              authIndexRef.current.staff.add(email);
            }

            if (roleKey === "external") {
              const advisersArr = asArray(data.advisers);

              const permissionsArr = [
                ...asArray(data.permissions),
                ...asArray(data.userPermissions),
                ...asArray(data.permission),
                ...asArray(data.access),
                ...asArray(data.status),
                ...asArray(data.statuses),
                ...asArray(data.category),
                ...asArray(data.categories),
              ].filter(Boolean);

              const uniquePermissions = Array.from(new Set(permissionsArr));

              authIndexRef.current.external.set(email, {
                advisers: advisersArr,
                permissions: uniquePermissions,
                raw: data,
              });

              // console.log("[SignIn] Indexed external user", {
              //   email,
              //   advisers: advisersArr,
              //   permissions: uniquePermissions,
              //   rawStatus: data.status,
              //   rawPermissions: data.permissions,
              // });
            }

            if (roleKey === "administrators") {
              const advisersArr = asArray(data.advisers);

              authIndexRef.current.administrators.set(email, {
                advisers: advisersArr,
                raw: data,
              });
            }
          });

          authIndexRef.current.readyRoles.add(roleKey);

          const allReady =
            authIndexRef.current.readyRoles.has("advisers") &&
            authIndexRef.current.readyRoles.has("staff") &&
            authIndexRef.current.readyRoles.has("external") &&
            authIndexRef.current.readyRoles.has("administrators");

          // console.log("[SignIn] Ready role status", {
          //   readyRoles: Array.from(authIndexRef.current.readyRoles),
          //   allReady,
          //   counts: {
          //     advisers: authIndexRef.current.advisers.size,
          //     staff: authIndexRef.current.staff.size,
          //     external: authIndexRef.current.external.size,
          //     administrators: authIndexRef.current.administrators.size,
          //   },
          // });

          setAuthReady(allReady);

          // console.groupEnd();
        },
        (error) => {
          // console.error(`[SignIn] Failed to load ${roleKey}`, error);

          authIndexRef.current.readyRoles.delete(roleKey);
          setAuthReady(false);
        },
      );

      unsubs.push(unsub);
    };

    watch("advisers", ["Authorized Members", "Advisers", "Data"]);
    watch("staff", ["Authorized Members", "Staff", "Data"]);
    watch("external", ["Authorized Members", "External", "Data"]);
    watch("administrators", ["Authorized Members", "Administrators", "Data"]);

    // console.groupEnd();

    return () => {
      // console.log("[SignIn] Unsubscribing auth index listeners");
      unsubs.forEach((unsub) => unsub());
    };
  }, []);

  // useEffect(() => {
  //   console.log("[SignIn] authReady changed", {
  //     authReady,
  //     readyRoles: Array.from(authIndexRef.current.readyRoles),
  //   });
  // }, [authReady]);

  const isEmailAuthorized = (email) => {
    // console.group("[SignIn] Checking email authorisation");

    const idx = authIndexRef.current;

    const allReady =
      idx.readyRoles.has("advisers") &&
      idx.readyRoles.has("staff") &&
      idx.readyRoles.has("external") &&
      idx.readyRoles.has("administrators");

    // console.log("[SignIn] Auth index readiness", {
    //   email,
    //   allReady,
    //   readyRoles: Array.from(idx.readyRoles),
    //   counts: {
    //     advisers: idx.advisers.size,
    //     staff: idx.staff.size,
    //     external: idx.external.size,
    //     administrators: idx.administrators.size,
    //   },
    // });

    if (!allReady) {
      // console.warn("[SignIn] Auth index not ready yet");
      // console.groupEnd();
      return "__NOT_READY__";
    }

    const normalizedEmail = normaliseEmail(email);

    // console.log("[SignIn] Normalised email", {
    //   originalEmail: email,
    //   normalizedEmail,
    // });

    if (idx.external.has(normalizedEmail)) {
      const externalData = idx.external.get(normalizedEmail) || {};

      const allAdvisers = Array.isArray(externalData.advisers)
        ? externalData.advisers
        : [];

      const externalPermissions = Array.isArray(externalData.permissions)
        ? externalData.permissions
        : [];

      // console.log("[SignIn] Matched External user", {
      //   normalizedEmail,
      //   allAdvisers,
      //   externalPermissions,
      //   raw: externalData.raw,
      // });

      setAdminAdviserMails(allAdvisers);

      localStorage.setItem(
        "externalPermissions",
        JSON.stringify(externalPermissions),
      );

      localStorage.setItem("adminAdviserMails", JSON.stringify(allAdvisers));

      // console.log("[SignIn] Saved external data to localStorage", {
      //   externalPermissions: JSON.parse(
      //     localStorage.getItem("externalPermissions") || "[]",
      //   ),
      //   adminAdviserMails: JSON.parse(
      //     localStorage.getItem("adminAdviserMails") || "[]",
      //   ),
      // });

      //  console.groupEnd();
      return "External";
    }

    if (idx.advisers.has(normalizedEmail)) {
      const adviserData = idx.advisers.get(normalizedEmail) || {};

      if (
        adviserData.isAppointed === false &&
        adviserData.isAccessAllowed !== true
      ) {
        return null;
      }

      // console.log("[SignIn] Matched Adviser user", { normalizedEmail });
      // console.groupEnd();
      return "Advisers";
    }

    if (idx.staff.has(normalizedEmail)) {
      //  console.log("[SignIn] Matched Staff user", { normalizedEmail });
      // console.groupEnd();
      return "Staff";
    }

    if (idx.administrators.has(normalizedEmail)) {
      const adminData = idx.administrators.get(normalizedEmail) || {};

      const allAdvisers = Array.isArray(adminData.advisers)
        ? adminData.advisers
        : [];

      // console.log("[SignIn] Matched Administrator user", {
      //   normalizedEmail,
      //   allAdvisers,
      //   raw: adminData.raw,
      // });

      setAdminAdviserMails(allAdvisers);

      localStorage.setItem("adminAdviserMails", JSON.stringify(allAdvisers));

      // console.groupEnd();
      return "Administrators";
    }

    // console.warn("[SignIn] Email not authorised", {
    //   normalizedEmail,
    // });

    // console.groupEnd();
    return null;
  };

  const getEmailFromUser = (user) => {
    // console.log("[SignIn] Getting email from auth user", {
    //   directEmail: user?.email,
    //   providerData: user?.providerData,
    // });

    if (user.email) return user.email;

    if (user.providerData?.length > 0) {
      for (const provider of user.providerData) {
        if (provider.email) return provider.email;
      }
    }

    return null;
  };

const getFirstVisiblePortalRoute = (sections) => {
  const routeOrder = [
    "my-dashboard",
    "members-hub",
    "check2",
    "tools-calculators",
    "loa-tracker-analysis",
    "marketing-promotions",
    "portal-administration",
  ];

  for (const key of routeOrder) {
    if (sections[key]?.show) {
      return sections[key].route;
    }
  }

  return "/";
};

const handlePostLoginNavigation = async ({ role, email }) => {
  const normalisedEmailValue = normaliseEmail(email);

  const userPermissions =
    role === "External"
      ? safeParseArray(localStorage.getItem("externalPermissions"))
      : safeParseArray(localStorage.getItem("userPermissions"));

  const adviserStatus = safeParseArray(localStorage.getItem("adviserStatus"));
  const adminStatus = safeParseArray(localStorage.getItem("adminStatus"));

  const canSeeAML = await canAccessAML({
    userRole: role,
    userPermissions,
    userEmail: normalisedEmailValue,
  });

  const sections = getPortalSectionConfig({
    userRole: role,
    userPermissions,
    adviserStatus,
    adminStatus,
    canSeeAML,
  });

  const route = getFirstVisiblePortalRoute(sections);

  navigate(route, { replace: true });
};

  const completeSignIn = async (user) => {
    // console.group("[SignIn] Completing sign-in");

    const email = getEmailFromUser(user);

    // console.log("[SignIn] Authenticated Firebase user", {
    //   uid: user?.uid,
    //   displayName: user?.displayName,
    //   email,
    //   providerData: user?.providerData,
    // });

    if (!email) {
      // console.error("[SignIn] No email returned from auth provider", {
      //   user,
      // });

      // alert("Error: Unable to retrieve email from authentication.");
      // console.groupEnd();
      return;
    }

    // console.log("[SignIn] Clearing previous local/session storage");
    localStorage.clear();
    sessionStorage.clear();

    const role = isEmailAuthorized(email);

    // console.log("[SignIn] Authorisation result", {
    //   email,
    //   role,
    // });

    if (role === "__NOT_READY__") {
      // alert("Please try again in a moment. User access is still loading.");
      // console.groupEnd();
      return;
    }

    if (!role) {
      alert("Your email is not authorized to sign in.");
      // console.groupEnd();
      return;
    }

    setUserRole(role);
    setUserEmail(email);

    localStorage.setItem("userRole", role);
    localStorage.setItem("userEmail", normaliseEmail(email));

    // console.log("[SignIn] Saved role/email", {
    //   userRole: localStorage.getItem("userRole"),
    //   userEmail: localStorage.getItem("userEmail"),
    //   externalPermissions: safeParseArray(
    //     localStorage.getItem("externalPermissions"),
    //   ),
    //   adminAdviserMails: safeParseArray(
    //     localStorage.getItem("adminAdviserMails"),
    //   ),
    // });

    await handlePostLoginNavigation({
      role,
      email: normaliseEmail(email),
    });

    // console.groupEnd();
  };

  const handleGoogleSignIn = async () => {
    // console.group("[SignIn] Google sign-in started");

    try {
      const result = await signInWithPopup(auth, googleProvider);

      //  console.log("[SignIn] Google sign-in success", {
      //   user: result.user,
      // });

      await completeSignIn(result.user);
    } catch (error) {
      // console.error("[SignIn] Error during Google Sign-In", error);
    } finally {
      // console.groupEnd();
    }
  };

  const handleMicrosoftSignIn = async () => {
    // console.group("[SignIn] Microsoft sign-in started");

    const microsoftProvider = new OAuthProvider("microsoft.com");
    microsoftProvider.setCustomParameters({ prompt: "select_account" });

    try {
      const result = await signInWithPopup(auth, microsoftProvider);

      // console.log("[SignIn] Microsoft sign-in success", {
      //   user: result.user,
      // });

      await completeSignIn(result.user);
    } catch (error) {
      // console.error("[SignIn] Error during Microsoft Sign-In", error);
    } finally {
      // console.groupEnd();
    }
  };

  return (
    <div className="SignIn-Container">
      <Helmet>
        <title>Adviser Portal - Rosemount Financial Solutions (IFA) Ltd</title>
        <meta
          name="description"
          content="Sign in to the Rosemount Financial Solutions (IFA) Ltd Adviser Portal"
        />
      </Helmet>

      <img src={Logo} alt="Logo" />

      <div className="SignIn-Form">
        <SignInButton
          imgSrc={GoogleIcon}
          title="Sign in with Google"
          onClick={handleGoogleSignIn}
          disabled={!authReady}
        />

        <SignInButton
          imgSrc={MicrosoftIcon}
          title="Sign in with Microsoft"
          onClick={handleMicrosoftSignIn}
          disabled={!authReady}
        />
      </div>
    </div>
  );
};

export default SignIn;
